// After a wholesale tier's minimum is saved, send the new minimum to Faire
// for every product in that tier, a few at a time, with a progress toast
// ("Updating Faire minimums for Core (5 of 20 done)…"). Products that
// aren't on Faire are skipped; a product that fails is named and the rest
// carry on. Faire can take a few minutes to show the change.

import { toast } from '../lib/shadcn/sonner'
import { listNames } from './syncResults'

export type FaireTierProduct = { sku: string; label_name: string }

type SetMinimumTrigger = (params: { sku: string; min_qty: number }) => Promise<unknown>

type SetMinimumResult = { minimum?: unknown; status?: unknown }

// Products updated at the same time.
const AT_ONCE = 4

export async function pushFaireMinimums(
  tier: string,
  minQty: number,
  products: FaireTierProduct[],
  trigger: SetMinimumTrigger,
): Promise<void> {
  if (products.length === 0) return
  const id = toast.loading(`Updating Faire minimums for ${tier} (0 of ${products.length} done)…`)
  let updated = 0
  let unchanged = 0
  let notOnFaire = 0
  let minimum: number | null = null
  const failed: string[] = []
  let finished = 0
  let next = 0

  async function pushOne(p: FaireTierProduct) {
    try {
      const res = ((await trigger({ sku: p.sku, min_qty: minQty })) ?? {}) as SetMinimumResult
      if (typeof res.minimum === 'number') minimum = res.minimum
      if (res.status === 'updated') updated++
      else if (res.status === 'unchanged') unchanged++
      else notOnFaire++
    } catch {
      failed.push(p.label_name)
    }
    finished++
    if (finished < products.length) {
      toast.loading(`Updating Faire minimums for ${tier} (${finished} of ${products.length} done)…`, { id })
    }
  }
  async function worker() {
    while (next < products.length) await pushOne(products[next++]!)
  }
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, products.length) }, () => worker()))
  const order = products.map(p => p.label_name)
  failed.sort((a, b) => order.indexOf(a) - order.indexOf(b))

  const onFaire = updated + unchanged
  const plural = (n: number) => `${n} product${n === 1 ? '' : 's'}`
  const parts: string[] = []
  if (onFaire > 0) {
    parts.push(
      `Faire minimum for ${tier} is now ${minimum ?? minQty} on ${plural(onFaire)}` +
        (unchanged > 0 && updated > 0 ? ` (${unchanged} already had it)` : '') +
        (minimum != null && minimum !== minQty ? ', rounded up to whole cases of 6.' : '.'),
    )
  }
  if (notOnFaire > 0) parts.push(`${plural(notOnFaire)} in ${tier} ${notOnFaire === 1 ? "isn't" : "aren't"} on Faire.`)
  if (failed.length > 0) {
    parts.push(`Couldn't update ${listNames(failed)} on Faire. Save the tier again to retry.`)
  }
  const message = parts.join(' ') || `No ${tier} products are on Faire.`
  if (failed.length > 0) toast.error(message, { id, duration: 20000 })
  else toast.success(message, { id, duration: 10000 })
}
